import { alpha, Box, Grid } from "@mui/material";
import { observer } from "mobx-react";
import { useState } from "react";
import BaseObjectStore from "./BaseObjectStore";
import { SignalsObjectType } from "../SignalsModels";
import SmallCard from "./SmallCard/SmallCard";
import SmallCardStore from "./SmallCard/SmallCardStore";
import MontageCard from "./MontageCard/MontageCard";
import MontageCardStore from "./MontageCard/MontageCardStore";
import TextCard from "./TextCard/TextCard";
import TextCardStore from "./TextCard/TextCardStore";
import ChartCard from "./ChartCard/ChartCard";
import ChartCardStore from "./ChartCard/ChartCardStore";
import TableCard from "./TableCard/TableCard";
import TableCardStore from "./TableCard/TableCardStore";
import UniqueCardStore from "./UniqueCard/UniqueCardStore";
import { isContentDrivenCard } from "../Dashboard/Layout/cardSizes";
import { useCardLayout } from "../Dashboard/Layout/CardLayoutContext";
import { CARD_CONTENT_ATTRIBUTE, CARD_RADIUS } from "../Dashboard/Layout/grid";
import { LayoutGrip } from "../Dashboard/Layout/LayoutGrip";
import { useObservedHeight } from "../Dashboard/Layout/useObservedHeight";

/**
 * The element whose children are a content-driven card's content: the panel
 * marked with `CARD_CONTENT_ATTRIBUTE` if the card has one, else its root.
 */
const contentContainer = (card: HTMLElement): HTMLElement | null =>
    card.querySelector<HTMLElement>(`[${CARD_CONTENT_ATTRIBUTE}]`) ??
    (card.firstElementChild as HTMLElement | null);

/**
 * The height a card's content needs: its children plus vertical padding.
 * Children are measured unrounded, because `offsetHeight` rounds to whole
 * pixels and reports a 92.92px box as 93px, a whole row more. The layout
 * store drops the fraction.
 */
const naturalHeight = (card: HTMLElement): number | null => {
    const container = contentContainer(card);
    if (!container) return null;

    const style = window.getComputedStyle(container);
    const padding =
        parseFloat(style.paddingTop || "0") +
        parseFloat(style.paddingBottom || "0");
    const height =
        [...container.children].reduce(
            (total, child) => total + child.getBoundingClientRect().height,
            0,
        ) + padding;

    return height > 0 ? height : null;
};

const watchContent = (card: HTMLElement): Element[] => {
    const container = contentContainer(card);
    return container ? [card, container, ...container.children] : [card];
};

const watchCardSubtree = (card: HTMLElement) => ({
    target: card,
    subtree: true,
});

export const SignalsBaseObjectWrapper = ({
    componentStore,
    observerRef,
}: {
    componentStore: BaseObjectStore<any, any>;
    observerRef?: (el: HTMLElement | null) => void;
}) => {
    const cardLayout = useCardLayout();
    const layoutStore = cardLayout?.layoutStore;
    const canArrange = cardLayout?.canArrange ?? false;
    const isEditing = cardLayout?.isEditing ?? false;
    const objectId = componentStore.id;
    const [cardElement, setCardElement] = useState<HTMLDivElement | null>(null);

    // Measured during a gesture too: the store holds rendered heights still
    // until the gesture ends, and the live floor it reads from this is what
    // stops a resize at content that has started to wrap.
    useObservedHeight({
        root: cardElement,
        measure: naturalHeight,
        onReport:
            layoutStore && isContentDrivenCard(componentStore.objectType)
                ? (height) => layoutStore.setMeasuredHeight(objectId, height)
                : undefined,
        watch: watchContent,
        mutations: watchCardSubtree,
    });

    return (
        <Grid
            ref={observerRef}
            sx={(theme) => ({
                position: "relative",
                display: "flex",
                alignItems: "stretch",
                width: "100%",
                height: "100%",
                minWidth: 0,
                // Marks the card as a block while arranging. A pseudo-element
                // with pointer events off, so it can never sit between the
                // pointer and the resize handle.
                ...(canArrange && {
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        zIndex: 4,
                        pointerEvents: "none",
                        borderRadius: CARD_RADIUS,
                        backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.04,
                        ),
                        border: `1px dashed ${alpha(
                            theme.palette.primary.main,
                            0.4,
                        )}`,
                    },
                }),
            })}
        >
            <Box
                ref={setCardElement}
                // While arranging, the card's own controls are out of reach of
                // both the pointer and the keyboard, so a slightly wrong click
                // or an arrow key cannot change what the dashboard shows, and
                // no card can be deleted or pinned mid-arrangement. That holds
                // even after the window narrows too far to drag. The grip and
                // resize handle sit outside this box.
                {...(isEditing ? { inert: "" } : {})}
                sx={{ width: "100%", minWidth: 0, height: "100%" }}
            >
                <ComponentRender componentStore={componentStore} />
            </Box>
            <CardGrip componentStore={componentStore} />
        </Grid>
    );
};

/** Text and montage cards can have no title, so the grip names their type. */
const UNTITLED_CARD_NAMES: Partial<Record<SignalsObjectType, string>> = {
    [SignalsObjectType.TextCard]: "text card",
    [SignalsObjectType.MontageCard]: "montage card",
    [SignalsObjectType.SmallCard]: "small card",
    [SignalsObjectType.Table]: "table",
    [SignalsObjectType.Chart]: "chart",
};

const gripName = (componentStore: BaseObjectStore<any, any>) =>
    componentStore.title?.trim() ||
    (UNTITLED_CARD_NAMES[componentStore.objectType] ?? "card");

/** The card's grip, whose label follows the card's title as it is renamed. */
const CardGrip = observer(
    ({ componentStore }: { componentStore: BaseObjectStore<any, any> }) => (
        <LayoutGrip
            objectId={componentStore.id}
            label={`Move or resize ${gripName(componentStore)}`}
        />
    ),
);

const ComponentRender = ({
    componentStore,
}: {
    componentStore: BaseObjectStore<any, any>;
}) => {
    // Render correct component based on object type
    switch (componentStore.objectType) {
        case SignalsObjectType.SmallCard:
            return <SmallCard store={componentStore as SmallCardStore} />;
        case SignalsObjectType.MontageCard:
            return <MontageCard store={componentStore as MontageCardStore} />;
        case SignalsObjectType.TextCard:
            return <TextCard store={componentStore as TextCardStore} />;
        case SignalsObjectType.Chart:
            return <ChartCard store={componentStore as ChartCardStore} />;
        case SignalsObjectType.Table:
            return <TableCard store={componentStore as TableCardStore} />;
        case SignalsObjectType.Unique:
            const uniqueStore = componentStore as UniqueCardStore;
            const UniqueComponent = uniqueStore.getComponent();
            return <UniqueComponent cardStore={uniqueStore} />;
        default:
            return (
                <div>
                    Unsupported object type - {componentStore.objectType} -{" "}
                    {componentStore.title} - {componentStore.order}
                </div>
            );
    }
};
